import { markFontFor, type MarkFont, type TextMeasure } from '@provenance/shared';
import { useEffect, useState } from 'react';

/**
 * Measures a text mark exactly as the stamping engine will print it (T-125).
 *
 * Uses pdf-lib's own `StandardFontEmbedder` — the object behind every `PDFFont` the engine
 * draws with — so the width here is the width on the certificate, not an estimate. The old
 * canvas guessed half an em per character, which is close for "0" and wrong for "M".
 *
 * `apps/api/src/modules/pdf/mark-parity.spec.ts` makes the same three calls and checks them
 * against the engine's real placements. Keep the two in step.
 *
 * Loaded on demand: pdf-lib is only needed on the stamping screen, and nowhere else in the
 * console should pay for it.
 */
export type TextMeasurer = (text: string, fontFamily: string, fontSize: number) => TextMeasure;

interface FontEmbedder {
  widthOfTextAtSize(text: string, size: number): number;
  heightOfFontAtSize(size: number, options?: { descender?: boolean }): number;
}

let loading: Promise<TextMeasurer> | null = null;

export function loadTextMeasurer(): Promise<TextMeasurer> {
  loading ??= import('pdf-lib').then(({ StandardFontEmbedder }) => {
    const embedders = new Map<MarkFont, FontEmbedder>();
    const embedderFor = (font: MarkFont): FontEmbedder => {
      let embedder = embedders.get(font);
      if (!embedder) {
        // pdf-lib types the argument as its FontNames enum, whose values are these names.
        embedder = StandardFontEmbedder.for(font as never);
        embedders.set(font, embedder);
      }
      return embedder;
    };

    return (text, fontFamily, fontSize) => {
      const font = embedderFor(markFontFor(fontFamily));
      const height = font.heightOfFontAtSize(fontSize);
      let width: number;
      try {
        width = font.widthOfTextAtSize(text, fontSize);
      } catch {
        // A character the standard fonts cannot encode. The engine will refuse it at
        // stamping too; an estimate keeps the mark visible here so it can be fixed.
        width = text.length * fontSize * 0.5;
      }
      return { width, height, descent: height - font.heightOfFontAtSize(fontSize, { descender: false }) };
    };
  });
  return loading;
}

/** The measurer once pdf-lib has loaded; null for the moment before. */
export function useTextMeasurer(): TextMeasurer | null {
  const [measurer, setMeasurer] = useState<TextMeasurer | null>(null);
  useEffect(() => {
    let live = true;
    void loadTextMeasurer().then((m) => {
      if (live) setMeasurer(() => m);
    });
    return () => {
      live = false;
    };
  }, []);
  return measurer;
}

export interface PixelSize {
  width: number;
  height: number;
}

/**
 * The pixel size of each image a seal or signature mark prints.
 *
 * Reading `naturalWidth` needs no CORS, so the object-storage URLs work as they are. The
 * engine sizes an image mark from exactly this, so the canvas must too.
 */
export function useImageSizes(urls: Array<string | null | undefined>): Record<string, PixelSize> {
  const [sizes, setSizes] = useState<Record<string, PixelSize>>({});
  const key = [...new Set(urls.filter((u): u is string => Boolean(u)))].sort().join('\n');

  useEffect(() => {
    let live = true;
    for (const url of key ? key.split('\n') : []) {
      if (sizes[url]) continue;
      const img = new Image();
      img.onload = () => {
        if (live) setSizes((prev) => ({ ...prev, [url]: { width: img.naturalWidth, height: img.naturalHeight } }));
      };
      img.src = url;
    }
    return () => {
      live = false;
    };
    // Keyed on the set of URLs; `sizes` is read only to skip ones already known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return sizes;
}
